import React, { useState } from 'react';
import { User, Lock, ArrowRight, Mail, Phone, MapPin, Calendar, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
export function LoginRegister() {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 1500);
  };
  return <div className="min-h-screen bg-gray-50 pt-40 lg:pt-52 pb-20" data-id="element-952">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-953">
        {/* Page Header */}
        <div className="text-center mb-8" data-id="element-954">
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-955">
            Student Portal
          </h1>
          <p className="text-lg text-gray-600" data-id="element-956">
            Access your on-demand classes or register to create an account
          </p>
        </div>

        {/* Important Notice */}
        <div className="mb-8 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl flex items-start gap-3 max-w-2xl mx-auto" data-id="element-957">
          <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-958" />
          <div data-id="element-959">
            <p className="text-sm font-semibold text-blue-900 mb-1" data-id="element-960">
              For On-Demand Classes Only
            </p>
            <p className="text-sm text-blue-800" data-id="element-961">
              This registration is for <strong data-id="element-962">On-Demand Classes only</strong>.
              If you're taking Zoom or In-Person classes, you do not need to
              register here.
            </p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-8" data-id="element-963">
          <div className="inline-flex bg-white rounded-lg shadow-sm border border-gray-200 p-1" data-id="element-964">
            <button onClick={() => setActiveTab('login')} className={`px-8 py-3 rounded-md font-semibold transition-all ${activeTab === 'login' ? 'bg-brand-purple-600 text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-965">
              Log In
            </button>
            <button onClick={() => setActiveTab('register')} className={`px-8 py-3 rounded-md font-semibold transition-all ${activeTab === 'register' ? 'bg-brand-purple-600 text-white shadow-md' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-966">
              Register
            </button>
          </div>
        </div>

        {/* Login Form */}
        {activeTab === 'login' && <Card className="max-w-md mx-auto p-8" data-id="element-967">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-968">
              Log In to Your Account
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-969">
              <div data-id="element-970">
                <label htmlFor="login-username" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-971">
                  Username
                </label>
                <div className="relative" data-id="element-972">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-973">
                    <User className="h-5 w-5 text-gray-400" data-id="element-974" />
                  </div>
                  <input type="text" id="login-username" className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" placeholder="Enter your username" required data-id="element-975" />
                </div>
              </div>

              <div data-id="element-976">
                <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-977">
                  Password
                </label>
                <div className="relative" data-id="element-978">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-979">
                    <Lock className="h-5 w-5 text-gray-400" data-id="element-980" />
                  </div>
                  <input type="password" id="login-password" className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" placeholder="••••••••" required data-id="element-981" />
                </div>
              </div>

              <Button type="submit" className="w-full font-semibold" size="lg" isLoading={isLoading} data-id="element-982">
                Log In <ArrowRight className="ml-2 h-4 w-4" data-id="element-983" />
              </Button>
            </form>

            <div className="mt-6 text-center" data-id="element-984">
              <a href="/14.html" className="text-sm text-gray-600 hover:text-brand-purple-600 hover:underline" data-id="element-985">
                Forgot your password?
              </a>
            </div>
          </Card>}

        {/* Registration Form */}
        {activeTab === 'register' && <Card className="max-w-2xl mx-auto p-8" data-id="element-986">
            <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center" data-id="element-987">
              Create Your Account
            </h2>
            <p className="text-sm text-gray-600 mb-6 text-center" data-id="element-988">
              Register for on-demand classes
            </p>

            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-989">
              {/* Personal Information */}
              <div data-id="element-990">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-991">
                  <User className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-992" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-993">
                  <div data-id="element-994">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-995">
                      Title
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-996">
                      <option data-id="element-997">Mr.</option>
                      <option data-id="element-998">Ms.</option>
                      <option data-id="element-999">Mrs.</option>
                      <option data-id="element-1000">Dr.</option>
                    </select>
                  </div>
                  <div data-id="element-1001">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1002">
                      First Name<span className="text-red-500" data-id="element-1003">*</span>
                    </label>
                    <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1004" />
                  </div>
                  <div data-id="element-1005">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1006">
                      Last Name<span className="text-red-500" data-id="element-1007">*</span>
                    </label>
                    <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1008" />
                  </div>
                  <div data-id="element-1009">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1010">
                      Company
                    </label>
                    <input type="text" className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1011" />
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div data-id="element-1012">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1013">
                  <MapPin className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1014" />
                  Billing Address
                </h3>
                <div className="space-y-4" data-id="element-1015">
                  <div data-id="element-1016">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1017">
                      Address 1<span className="text-red-500" data-id="element-1018">*</span>
                    </label>
                    <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1019" />
                  </div>
                  <div data-id="element-1020">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1021">
                      Address 2
                    </label>
                    <input type="text" className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1022" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1023">
                    <div data-id="element-1024">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1025">
                        City<span className="text-red-500" data-id="element-1026">*</span>
                      </label>
                      <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1027" />
                    </div>
                    <div data-id="element-1028">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1029">
                        State/Province<span className="text-red-500" data-id="element-1030">*</span>
                      </label>
                      <select required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1031">
                        <option value="" data-id="element-1032">Select...</option>
                        <option value="AZ" data-id="element-1033">Arizona</option>
                        <option value="CA" data-id="element-1034">California</option>
                        <option value="NV" data-id="element-1035">Nevada</option>
                      </select>
                    </div>
                    <div data-id="element-1036">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1037">
                        Zip<span className="text-red-500" data-id="element-1038">*</span>
                      </label>
                      <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1039" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div data-id="element-1040">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1041">
                  <Phone className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1042" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1043">
                  <div data-id="element-1044">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1045">
                      Phone<span className="text-red-500" data-id="element-1046">*</span>
                    </label>
                    <input type="tel" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1047" />
                  </div>
                  <div data-id="element-1048">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1049">
                      Email<span className="text-red-500" data-id="element-1050">*</span>
                    </label>
                    <input type="email" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1051" />
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div data-id="element-1052">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1053">
                  Additional Information
                </h3>
                <div className="space-y-4" data-id="element-1054">
                  <div data-id="element-1055">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1056">
                      How did you hear about us?
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1057">
                      <option value="" data-id="element-1058">Select...</option>
                      <option data-id="element-1059">Google Search</option>
                      <option data-id="element-1060">Social Media</option>
                      <option data-id="element-1061">Referral</option>
                      <option data-id="element-1062">Other</option>
                    </select>
                  </div>
                  <div data-id="element-1063">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1064">
                      Are you on Facebook?
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1065">
                      <option value="" data-id="element-1066">Select...</option>
                      <option data-id="element-1067">Yes</option>
                      <option data-id="element-1068">No</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Username & Password */}
              <div data-id="element-1069">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1070">
                  <Lock className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1071" />
                  Create Your Username & Password
                </h3>
                <div className="space-y-4" data-id="element-1072">
                  <div data-id="element-1073">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1074">
                      Username<span className="text-red-500" data-id="element-1075">*</span>
                    </label>
                    <input type="text" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1076" />
                  </div>
                  <div data-id="element-1077">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1078">
                      Password<span className="text-red-500" data-id="element-1079">*</span>
                    </label>
                    <input type="password" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1080" />
                    <p className="text-xs text-gray-500 mt-1" data-id="element-1081">
                      Must be at least 8 characters
                    </p>
                  </div>
                  <div data-id="element-1082">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1083">
                      Confirm Password<span className="text-red-500" data-id="element-1084">*</span>
                    </label>
                    <input type="password" required className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1085" />
                  </div>
                </div>
              </div>

              {/* License Expiration Date */}
              <div data-id="element-1086">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" data-id="element-1087">
                  <Calendar className="w-5 h-5 mr-2 text-brand-purple-600" data-id="element-1088" />
                  License Expiration Date
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-1089">
                  <div data-id="element-1090">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1091">
                      Month
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1092">
                      <option data-id="element-1093">January</option>
                      <option data-id="element-1094">February</option>
                      <option data-id="element-1095">March</option>
                      <option data-id="element-1096">April</option>
                      <option data-id="element-1097">May</option>
                      <option data-id="element-1098">June</option>
                      <option data-id="element-1099">July</option>
                      <option data-id="element-1100">August</option>
                      <option data-id="element-1101">September</option>
                      <option data-id="element-1102">October</option>
                      <option data-id="element-1103">November</option>
                      <option data-id="element-1104">December</option>
                    </select>
                  </div>
                  <div data-id="element-1105">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1106">
                      Year
                    </label>
                    <select className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple-500 focus:border-brand-purple-500" data-id="element-1107">
                      <option data-id="element-1108">2024</option>
                      <option data-id="element-1109">2025</option>
                      <option data-id="element-1110">2026</option>
                      <option data-id="element-1111">2027</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-blue-600 mt-2" data-id="element-1112">
                  <a href="#" className="hover:underline" data-id="element-1113">
                    Click here to obtain your license expiration date from ADRE
                  </a>
                </p>
              </div>

              {/* Identity Confirmation */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-1114">
                <div className="flex items-start" data-id="element-1115">
                  <input type="checkbox" id="identity-confirm" required className="mt-1 h-4 w-4 text-brand-purple-600 focus:ring-brand-purple-500 border-gray-300 rounded" data-id="element-1116" />
                  <label htmlFor="identity-confirm" className="ml-3 text-sm text-gray-700" data-id="element-1117">
                    <strong data-id="element-1118">I affirm this information to be true.</strong> The
                    Arizona Department of Real Estate requires that we confirm
                    your information and that you are the person taking the
                    classes. Incorporating a real person's name and license
                    number into a registration without the permission of both
                    parties, if you are NOT the person named in the
                    registration, we strongly recommend that you log out and not
                    continue. We reserve the right to use reasonable means to
                    verify your identity.
                  </label>
                </div>
              </div>

              {/* Privacy Statement */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1119">
                <h4 className="font-semibold text-blue-900 mb-2" data-id="element-1120">
                  Privacy Statement
                </h4>
                <p className="text-sm text-blue-800 mb-2" data-id="element-1121">
                  By registering with Desert Sage Seminars, you will create an
                  account by which we can administer your real estate license
                  renewal classes. Your information will be kept confidential
                  and not shared with any other party.
                </p>
                <p className="text-sm text-blue-800 mb-2" data-id="element-1122">
                  The Arizona Department of Real Estate requires that we confirm
                  your information and that you are the person taking the
                  classes.
                </p>
                <p className="text-sm text-blue-800" data-id="element-1123">
                  Payments for our classes are made through a 3rd party merchant
                  account secured by our site, and they do not share your credit
                  card information with us or any other party.
                </p>
              </div>

              {/* Submit Button */}
              <Button type="submit" className="w-full font-semibold" size="lg" isLoading={isLoading} data-id="element-1124">
                Create Account <CheckCircle2 className="ml-2 h-5 w-5" data-id="element-1125" />
              </Button>
            </form>
          </Card>}
      </div>
    </div>;
}